import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { AlertTriangle, Gavel, Shield } from "lucide-react";
import { analysesApi } from "@/lib/api";
import KpiCard from "@/components/KpiCard";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function ClausesAnalysis() {
  const { projectId } = useParams();
  const [clauses, setClauses] = useState(null);
  const [legal, setLegal] = useState(null);
  const [legalLoading, setLegalLoading] = useState(false);

  useEffect(() => {
    analysesApi.clauses(projectId).then(setClauses);
  }, [projectId]);

  const runLegal = async () => {
    setLegalLoading(true);
    try {
      const r = await analysesApi.legal(projectId);
      setLegal(r);
      toast.success("Analyse juridique terminée (cachée en base).");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    } finally { setLegalLoading(false); }
  };

  if (!clauses) return <div className="p-10 label-mono text-xs text-muted-foreground">Chargement…</div>;

  return (
    <div className="px-6 lg:px-10 py-8 max-w-7xl mx-auto w-full" data-testid="clauses-page">
      <div className="label-mono text-[10px] text-muted-foreground">ANALYSE DES CONVENTIONS</div>
      <h1 className="mt-1 font-serif font-bold text-4xl">Clauses & conditionnalités</h1>

      <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard testId="clauses-count" label="CLAUSES EXTRAITES" value={clauses.count} format="int" />
        <KpiCard testId="alerts-count" label="ALERTES" value={clauses.alerts?.length} format="int" band={clauses.alerts?.length > 0 ? "modere" : "soutenable"} />
        <KpiCard testId="critical-count" label="CRITIQUES" value={clauses.critical_count} format="int" band={clauses.critical_count > 0 ? "critique" : "soutenable"} />
        <KpiCard testId="non-concess" label="NON-CONCESSIONNELS" value={(clauses.clauses || []).filter((c) => c.grant_element_pct != null && c.grant_element_pct < 25).length} format="int" band="modere" />
      </div>

      {clauses.alerts?.length > 0 && (
        <div className="mt-6 border border-alert/50 bg-alert/5 rounded-sm p-5" data-testid="alerts-banner">
          <div className="flex items-start gap-3">
            <AlertTriangle className="text-alert shrink-0" />
            <div>
              <h3 className="font-serif font-bold">Alertes détectées automatiquement</h3>
              <ul className="mt-2 text-sm space-y-1">
                {clauses.alerts.slice(0, 8).map((a, i) => (
                  <li key={i} className="text-foreground">
                    <strong className="font-mono text-alert">{a.severity.toUpperCase()}</strong> — {a.msg}
                    {a.clause_id && <span className="text-muted-foreground text-xs"> (clause {a.clause_id})</span>}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      <div className="mt-8">
        <h2 className="font-serif font-bold text-2xl">Tableau comparatif</h2>
        <div className="mt-3 overflow-x-auto border border-border rounded-sm">
          <table className="w-full text-xs" data-testid="clauses-table">
            <thead className="bg-muted/30 label-mono text-[9px] text-muted-foreground">
              <tr>
                <th className="text-left p-3">ID</th>
                <th className="text-left p-3">CRÉANCIER</th>
                <th className="text-left p-3">TYPE</th>
                <th className="text-right p-3">MONTANT</th>
                <th className="text-right p-3">TAUX</th>
                <th className="text-right p-3">MATURITÉ</th>
                <th className="text-right p-3">ELEM-DON</th>
                <th className="text-right p-3">COÛT VITAL.</th>
                <th className="text-left p-3">RISQUE</th>
              </tr>
            </thead>
            <tbody>
              {(clauses.clauses || []).map((c, i) => (
                <tr key={i} className="border-t border-border">
                  <td className="p-3 font-mono">{c.clause_id || `#${i}`}</td>
                  <td className="p-3">{c.creditor || "—"}</td>
                  <td className="p-3 label-mono text-[9px]">{c.clause_type}</td>
                  <td className="p-3 text-right font-mono">{fmt(c.amount)} {c.currency}</td>
                  <td className="p-3 text-right font-mono">{c.interest_rate}%</td>
                  <td className="p-3 text-right font-mono">{c.maturity_years} ans</td>
                  <td className="p-3 text-right font-mono">{c.grant_element_pct != null ? `${c.grant_element_pct}%` : "—"}</td>
                  <td className="p-3 text-right font-mono">{c.vitalist_cost_years != null ? `${c.vitalist_cost_years.toFixed(2)} ans/pers` : "—"}</td>
                  <td className="p-3">
                    <span className={`label-mono text-[9px] px-2 py-0.5 rounded-sm ${badgeCls(c.risk_level)}`}>
                      {(c.risk_level || "n/a").toUpperCase()}
                    </span>
                  </td>
                </tr>
              ))}
              {(clauses.clauses || []).length === 0 && (
                <tr><td colSpan={9} className="p-6 text-center text-muted-foreground">Aucune clause extraite.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Legal analysis */}
      <div className="mt-10 border border-gold/40 bg-gold/5 rounded-sm p-6" data-testid="legal-section">
        <div className="flex items-start gap-3">
          <Shield className="text-gold shrink-0 mt-1" />
          <div className="flex-1">
            <h3 className="font-serif font-bold text-xl">Analyse juridique (CNUCED + ONU Bohoslavsky)</h3>
            <p className="mt-2 text-xs text-muted-foreground max-w-2xl">
              Un seul appel GPT-4o groupé — le résultat est ensuite mis en cache.
              <strong className="text-foreground"> Document pédagogique — sans valeur juridictionnelle.</strong>
            </p>
            {!legal && (
              <Button onClick={runLegal} disabled={legalLoading || !clauses.count}
                      className="mt-4 bg-marine hover:bg-marine/90 text-white" data-testid="run-legal-analysis-btn">
                {legalLoading ? "Analyse en cours…" : "Lancer l'analyse juridique"}
              </Button>
            )}
            {legal && (
              <div className="mt-4 space-y-4">
                <div className="grid grid-cols-3 gap-3">
                  <InfoChip label="CNUCED" value={legal.cnuced_compliance} />
                  <InfoChip label="Droits humains" value={legal.human_rights_impact} />
                  <InfoChip label="Score risque souv." value={`${legal.sovereign_risk_score}/100`} />
                </div>
                <p className="text-sm italic border-l-2 border-marine dark:border-gold pl-4">
                  {legal.overall_assessment}
                </p>
                {legal.legal_risks?.length > 0 && (
                  <div>
                    <div className="label-mono text-[10px] text-muted-foreground mb-2">RISQUES JURIDIQUES</div>
                    <div className="space-y-2">
                      {legal.legal_risks.map((r, i) => (
                        <div key={i} className={`border-l-2 pl-4 py-1 text-xs ${
                          r.severity === "critique" ? "border-alert" :
                          r.severity === "eleve" ? "border-warn" : "border-muted-foreground"
                        }`}>
                          <div><strong>{r.risk_type}</strong> — <span className="label-mono text-[9px]">{r.severity?.toUpperCase()}</span></div>
                          <div className="text-muted-foreground mt-1">{r.legal_basis}</div>
                          <div className="mt-1 italic">{r.recommendation}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function InfoChip({ label, value }) {
  return (
    <div className="border border-border bg-card rounded-sm p-3">
      <div className="label-mono text-[9px] text-muted-foreground">{label}</div>
      <div className="mt-1 font-mono text-sm">{value || "—"}</div>
    </div>
  );
}

function badgeCls(level) {
  return {
    faible: "bg-success/10 text-success",
    modere: "bg-warn/10 text-warn",
    eleve: "bg-alert/10 text-alert",
    critique: "bg-alert text-white",
  }[level] || "bg-muted text-muted-foreground";
}

function fmt(n) { return n == null ? "—" : Number(n).toLocaleString("fr-FR"); }
